import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { StockReservationLineInput } from '../types/stockReservation';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface CreateReservationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instanceIds?: string[];
  items?: StockReservationLineInput[];
  reservationId?: string;
  onSuccess?: () => void;
}

const unitCount = (instanceIds: string[], items: StockReservationLineInput[]) =>
  items.length ? items.reduce((sum, line) => sum + line.quantity, 0) : instanceIds.length;

export default function CreateReservationModal({
  open,
  onOpenChange,
  instanceIds = [],
  items = [],
  reservationId,
  onSuccess,
}: CreateReservationModalProps) {
  const navigate = useNavigate();
  const adding = Boolean(reservationId);
  const [projectCode, setProjectCode] = useState('');
  const [durationDays, setDurationDays] = useState('14');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const units = unitCount(instanceIds, items);

  const reset = () => {
    setProjectCode('');
    setDurationDays('14');
    setError('');
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setSubmitting(true);
    setError('');
    try {
      const stock = items.length ? { items } : { instance_ids: instanceIds };
      const response = await fetch(
        adding ? `/api/stock-reservations/${reservationId}/items` : '/api/stock-reservations',
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(adding ? stock : {
            project_code: projectCode,
            duration_days: Number(durationDays),
            ...stock,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to reserve stock');
      reset();
      onOpenChange(false);
      onSuccess?.();
      navigate(`/reservations/${adding ? reservationId : data.data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reserve stock');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) reset(); onOpenChange(next); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{adding ? 'Add warehouse stock' : 'Reserve warehouse stock'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          <p className="text-sm text-muted-foreground">
            {units} warehouse unit{units === 1 ? '' : 's'}
            {items.length > 1 ? ` across ${items.length} products` : ''}.
            Supplier stock is not reserved.
          </p>
          {!adding && (
            <>
              <div className="space-y-2">
                <Label htmlFor="project_code">Project code</Label>
                <Input id="project_code" value={projectCode} onChange={(e) => setProjectCode(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration_days">Duration (days, max 90)</Label>
                <Input
                  id="duration_days"
                  type="number"
                  min={1}
                  max={90}
                  value={durationDays}
                  onChange={(e) => setDurationDays(e.target.value)}
                />
              </div>
            </>
          )}
          <div className="flex gap-4">
            <Button variant="secondary" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              className="flex-1"
              onClick={handleSubmit}
              disabled={submitting || units < 1 || (!adding && !projectCode.trim())}
            >
              {adding ? 'Add' : 'Reserve'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
